import React, { useMemo, useState } from 'react';
import { IconCoin, IconDownload, IconX } from '@tabler/icons-react';
import { countSolanaTokenCandidates, extractSolanaTokenAddresses } from './solanaTokenAddresses';
import './TokenWalletExport.css';

interface TokenWalletExportProps {
  loading: boolean;
  hasActivityWindow: boolean;
  premiumActive: boolean;
  onExport: (tokens: string[]) => Promise<boolean>;
}

const TokenWalletExport: React.FC<TokenWalletExportProps> = ({
  loading,
  hasActivityWindow,
  premiumActive,
  onExport,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const tokens = useMemo(() => extractSolanaTokenAddresses(text), [text]);
  const matchesCount = useMemo(() => countSolanaTokenCandidates(text), [text]);
  const duplicateCount = matchesCount - tokens.length;
  const overLimit = tokens.length > 5000;
  const canExport = tokens.length > 0 && !overLimit && duplicateCount === 0
    && hasActivityWindow && premiumActive && !loading;

  const startExport = async () => {
    if (!canExport) return;
    const started = await onExport(tokens);
    if (started) setOpen(false);
  };

  return (
    <div className={`sol-token-export ${open ? 'is-open' : ''}`}>
      <button
        className="sol-token-export-toggle"
        type="button"
        aria-expanded={open}
        aria-controls="sol-token-export-panel"
        onClick={() => setOpen((value) => !value)}
        disabled={loading}
      >
        <IconCoin size={14} /> Export from tokens
      </button>

      {open && (
        <div className="sol-token-export-panel" id="sol-token-export-panel">
          <div className="sol-token-export-head">
            <div><strong>Token mints</strong><span>1–5,000 valid Solana mint addresses.</span></div>
            <button type="button" aria-label="Close token export" onClick={() => setOpen(false)}><IconX size={15} /></button>
          </div>
          <textarea
            autoFocus
            aria-label="Solana token mints"
            value={text}
            maxLength={300000}
            placeholder={'So11111111111111111111111111111111111111112\nEPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'}
            onChange={(event) => setText(event.currentTarget.value)}
          />
          <div className="sol-token-export-footer">
            <div
              className={`sol-token-export-count ${!canExport && tokens.length > 0 ? 'has-warning' : ''}`}
              aria-label={`${tokens.length} unique ${tokens.length === 1 ? 'token' : 'tokens'} found`}
              aria-live="polite"
            >
              <span><strong>{tokens.length.toLocaleString()}</strong> unique {tokens.length === 1 ? 'token' : 'tokens'} found</span>
              {text.trim() && tokens.length === 0 && <small>No valid Solana mint addresses found.</small>}
              {duplicateCount > 0 && <small>Remove {duplicateCount.toLocaleString()} duplicate {duplicateCount === 1 ? 'address' : 'addresses'}.</small>}
              {overLimit && <small>Maximum is 5,000 token mints.</small>}
              {!hasActivityWindow && <small>Set Last trade time (minimum or maximum) in the preset first.</small>}
              {!premiumActive && <small>An active Premium subscription is required.</small>}
            </div>
            <button className="sol-token-export-submit" type="button" onClick={startExport} disabled={!canExport}>
              <IconDownload size={15} /> {loading ? 'Starting…' : 'Start export'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenWalletExport;
